import dayjs from 'dayjs';
import { toString } from 'lodash';

import { api } from '@/api';
import { API_URL } from '@/dtos';
import { getFileInBlob } from '@/utils/modifiers';

import { FileDtoWithMediaSource, FileStatus, InputFile } from '../FileManager.constants';

import { FileDtoPayload, FileStatusPayload, getStatus } from './useFilesList.query';

export const UNKNOWN_VALUE = '–';

export const addBacklightingFields = (status: FileStatus) => ({
  backlighting: status,
  backlightingTime: Date.now(),
});

interface FileDtoPayloadWithInputFile extends FileDtoPayload {
  file: InputFile;
}

const formatData = (file: FileDtoWithMediaSource, data?: FileDtoPayloadWithInputFile) => {
  const status = getStatus(data?.file_processing_status_type) as FileStatus;

  const partnersNames = [...(data?.partners_names || [])].filter((name) => Boolean(name));
  const employersNames = [...(data?.employers_names || [])].filter((name) => Boolean(name));

  return {
    id: toString(data?.id),
    temporalId: file.temporalId,
    name: data?.name || file.name,
    partnersNames: partnersNames.length ? partnersNames : file.partnersNames,
    employersNames: employersNames.length ? employersNames : file.employersNames,
    uploadedBy: file.uploadedBy || '',
    path: data?.file_path || '',
    extensionType: file.extensionType || '',
    type: data?.file_type || '',
    status,
    ...addBacklightingFields(status),
    createdAt: data?.created_at || file.createdAt === UNKNOWN_VALUE ? dayjs().format() : '',
    processedAt: data?.processed_at || file.processedAt || '',
    hasReuploading: false,
    isQualityGateError: data?.file_processing_status_type === FileStatusPayload.qualityGatesFailed,
  };
};

const useFilesUploadQuery = () => {
  const uploadFile = async (file: FileDtoWithMediaSource) => {
    try {
      const blob = await getFileInBlob(URL.createObjectURL(file.file));
      const formData = new FormData();

      formData.append('file', blob as Blob, file.name);
      const url = API_URL.FILES_CSV_IMPORT;
      // TODO(EL-33479): Server returns IFileImportResponse (from generated OpenAPI DTOs), but formatData
      // consumes the local FileDtoPayloadWithInputFile shape. Reconcile formatData/types with
      // IFileImportResponse and drop this explicit generic once aligned.
      const response = await api.post<FileDtoPayloadWithInputFile>(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: {
          dry_run: false,
          upload_way: 'UI',
        },
      });
      return {
        ...formatData(file, response?.data),
        file: file.file,
      } as FileDtoWithMediaSource;
    } catch {
      return {
        ...formatData(file, undefined),
        status: FileStatus.Uploading,
        backlighting: FileStatus.Uploading,
        backlightingTime: Date.now(),
        type: FileStatus.Unknown,
        file: file.file,
        hasReuploading: true,
      } as FileDtoWithMediaSource;
    }
  };

  return {
    uploadFiles: (files: FileDtoWithMediaSource[]) => Promise.all(files.map((file) => uploadFile(file))),
  };
};

export default useFilesUploadQuery;
